import moment from "moment";
import EnLang from "@/../../lang/en.json";
import BnLang from "@/../../lang/bn.json";

export default {
  methods: {
    /**
     * Generate full url.
     *
     * @param {String} path
     * @returns String
     */
    base_url(path) {
      if (!path) return "";
      const url = new URL(path, window.baseurl);
      return url.toString();
    },

    /**
     * Custom asset path.
     *
     * @param {String} path
     * @returns String
     */
    custom_asset(path) {
      if (!path) return "";
      return `${window.baseurl}/${path}`;
    },

    /**
     * Asset path with base URL.
     *
     * @param {String} path
     * @returns String
     */
    asset_file_url(path) {
      if (!path) return "/web_assets/img/default.png";
      const url = new URL(`shared/${path}`, window.location.origin);
      return url.toString();
    },

    /**
     * Set an alternative image URL if an error occurs on loading the actual image.
     *
     * @param {Event} event
     */
    image_load_error(event) {
      event.target.src = "/web_assets/img/default.png";
    },

    /**
     * Language translation.
     *
     * @param {Object} obj
     * @param {String|Object} field
     * @param {Array} lang_type
     * @returns String
     */
    locale_attr(obj, field, lang_type = ["en", "bn"]) {
      if (obj instanceof Object) {
        if (field instanceof Object) {
          const field_name = field[window.lang] ?? "";
          return obj[field_name] ?? "";
        }

        let replacement = window.lang == "en" ? lang_type[0] : lang_type[1];
        let field_name = field.replace(/\{lang\}/g, replacement);
        return obj[field_name] ?? "";
      }

      return "";
    },

    /**
     * Convert english to bengali.
     *
     * @param {number} str
     * @returns String
     */
    en_to_bn(str) {
      let en0 = [
        "AM",
        "PM",
        "am",
        "pm",
        "Day",
        "Month",
        "Year",
        "days",
        "months",
        "years",
      ];
      let bn0 = [
        "পূর্বাহ্ণ",
        "অপরাহ্ণ",
        "পূর্বাহ্ণ",
        "অপরাহ্ণ",
        "দিন",
        "মাস",
        "বছর",
        "দিন",
        "মাস",
        "বছর",
      ];

      let en1 = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      let bn1 = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];

      let en2 = [
        "Month",
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      let bn2 = [
        "মাস",
        "জানুয়ারি",
        "ফেব্রুয়ারি ",
        "মার্চ",
        "এপ্রিল",
        "মে",
        "জুন",
        "জুলাই",
        "আগস্ট",
        "সেপ্টেম্বর",
        "অক্টোবর",
        "নভেম্বর",
        "ডিসেম্বর",
      ];

      let en3 = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      let bn3 = [
        "জানুয়ারি",
        "ফেব্রুয়ারি ",
        "মার্চ",
        "এপ্রিল",
        "মে",
        "জুন",
        "জুলাই",
        "আগস্ট",
        "সেপ্টেম্বর",
        "অক্টোবর",
        "নভেম্বর",
        "ডিসেম্বর",
      ];

      let en4 = [
        "Saturday",
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
      ];
      let bn4 = ["শনি", "রবি", "সোম", "মঙ্গল", "বুধ", "বৃহস্পতি", "শুক্র"];

      let en5 = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"];
      let bn5 = ["শনি", "রবি", "সোম", "মঙ্গল", "বুধ", "বৃহস্পতি", "শুক্র"];

      let en = en0.concat(en1, en2, en3, en4, en5);
      let bn = bn0.concat(bn1, bn2, bn3, bn4, bn5);

      let output = String(str);

      if (window.lang == "bn") {
        en.forEach((word, index) => {
          let regex = new RegExp(word, "g");
          output = output.replace(regex, bn[index]);
        });

        return output;
      }

      return str;
    },

    /**
     * Date formatter.
     *
     * @param {Date|String} date
     * @param {String} format
     * @returns String
     */
    date_format(date, format = "YYYY-MM-DD") {
      return moment(date).format(format);
    },

    /**
     * Translate.
     *
     * @param {String} key
     * @returns String
     */
    __(key, params = {}) {
      const languages = { en: EnLang, bn: BnLang };
      let trans = languages[window.lang][key] || key;
      Object.keys(params).forEach((key) => {
        trans = String(trans).replace(`:${key}`, params[key] || "");
      });
      return trans;
    },

    /**
     * Get youtube video id.
     *
     * @param {String} path
     * @returns String
     */
    yt_video_id(path = "") {
      const segments = String(path).split("/");
      return segments.pop().replace("watch?v=", "");
    },

    /**
     * Get youtube video thumbnail url.
     *
     * @param {String} path
     * @returns String
     */
    yt_thumbnail(path = "") {
      const id = this.yt_video_id(path);
      return `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
    },

    /**
     * Get youtube video thumbnail url.
     *
     * @param {String} path
     * @returns String
     */
    yt_watch(path = "") {
      const id = this.yt_video_id(path);
      return `https://www.youtube.com/watch?v=${id}`;
    },

    /**
     * Copy to clipboard.
     *
     * @param {String} text
     * @param {Function} callback
     */
    copy(text, callback) {
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(text).then(callback).catch(console.error);
      } else {
        console.error("[Error]: Navigator clipboard not available.");
      }
    },

    /**
     * Get value from target using dot notation key.
     *
     * @param {Array|Object} target
     * @param {String} key
     * @returns any
     */
    data_get(target, key) {
      return String(key)
        .split(".")
        .reduce((curr, prev) => prev[curr], target);
    },

    rand_color_hex_code() {
      // Generate a random number between 0 and 16777215 (0xFFFFFF)
      const randomNumber = Math.floor(Math.random() * 16777215);
      // Convert the number to a hex string and pad with zeros if necessary
      return "#" + randomNumber.toString(16).padStart(6, "0");
    },

    /**
     * Observes the visibility of a specified element
     * and triggers a callback when the element becomes visible.
     *
     * @param {HTMLElement} el
     * @param {Function} callback
     * @param {Object} observerOptions
     * @param {Number} maxObservations
     */
    observe_visibility(
      el,
      callback,
      observerOptions = {},
      maxObservations = 1
    ) {
      const defaultOptions = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };

      const options = {
        ...defaultOptions,
        ...observerOptions,
      };

      let observationCount = 0;

      const observerCallback = (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback(entry, observer, entries);
            observationCount++;
            if (observationCount >= maxObservations) {
              observer.unobserve(entry.target);
            }
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, options);
      observer.observe(el);

      return {
        observer,
        disable: () => {
          observer.unobserve(el);
          observer.disconnect();
        },
      };
    },
  },
};
